// Added by Sejune Oh on 2020-02-24
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import React, { FC } from 'react'
import { createStyles, makeStyles } from '@material-ui/core'

// Helper
import Logger from '@/helper/Logger'
import { downloadFile } from '@/helper/util'

// UI
import { LCSStatisticStyleTypes } from '@/components/worklist/WebComponents/LCSStatisticWrapper'
import LegacyAppIcon from '@/legacy-polymer/LegacyAppIcon'

// API
import { reqExportLCSResult } from '@/service/study/lcsStatistics.service'
import { getDataStorageList } from '@/service/dataStorage.service'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
export interface StatisticExportButtonProps {
  /** DataStorage's DataToken */
  proxyToken: string
  /** Export할 데이터의 유무  */
  hasLCSData: boolean
  /** WC용 Styles  */
  classes?: LCSStatisticStyleTypes
}

/**
 *  현재 선택된 DataStorage의 LCS 통계정보를 Export 하기위한 버튼 UI 입니다.
 */
const StatisticExportButton: FC<StatisticExportButtonProps> = ({
  proxyToken,
  hasLCSData,
  classes: webComponentClassses,
  ...rest
}) => {
  const classes = webComponentClassses
    ? webComponentClassses['statisticexportbutton']
    : makeStyles(() => createStyles({ ...customStyles }))()

  const downloadCSVFile = async () => {
    // Export 할 데이터가 없습니다.
    if (!hasLCSData) return

    if (!proxyToken) {
      Logger.errLog('StatisticExportButton.downloadCSVFile -> ProxyToken 값이 없음.')
      alert(`Can not find data storage's token.`)
      return
    }

    try {
      // 현재 선택된 DataStorage의 정보를 가지고옵니다.
      const {
        data: { items: dataStorages },
      } = await getDataStorageList(`?filter=${JSON.stringify({ data_token: proxyToken })}`)
      if (dataStorages.length === 0) {
        Logger.errLog('StatisticExportButton.downloadCSVFile -> DataToken이 일치하는 Datastorage가 없음.')
        alert(`No matching datastorages found.`)
        return
      }

      // 생성되는 CSV File의 이름은 [DATE]_[STORAGE_NAME].csv 입니다.
      const filename = Date.now() + '_' + dataStorages[0].name + '.csv'

      // BE에서 CSV File을 생성합니다.
      const path = await reqExportLCSResult({ proxy_token: proxyToken }, filename)
      downloadFile(path, filename)
    } catch (e) {
      Logger.errLog('StatisticExportButton.downloadCSVFile -> CSV 파일을 생성하는데 실패.')
      alert(`Fail to create CSV file`)
      console.log(e)
    }
  }

  return (
    <div className={classes.wrapper} onClick={downloadCSVFile} data-disabled={!hasLCSData}>
      <span className={classes.iconWrapper} data-disabled={!hasLCSData}>
        <LegacyAppIcon iconSize={24} iconName={'cl:csv-export'} />
      </span>
    </div>
  )
}

export const customStyles: Record<string, any> = {
  wrapper: {
    height: '45px',
    width: '45px',
    position: 'relative',
    '&[data-disabled="false"]': {
      cursor: 'pointer',
      '&:hover svg': {
        fill: 'rgb(83, 184, 255)',
      },
    },
    '&[data-disabled="true"]': {
      cursor: 'not-allowed',
    },
  },
  iconWrapper: {
    position: 'absolute',
    right: 0,
    left: 0,
    top: 0,
    bottom: 0,
    width: '24px',
    height: '24px',
    margin: 'auto',
    '&[data-disabled="false"] svg': {
      fill: '#fff',
    },
    '&[data-disabled="true"] svg': {
      fill: '#a5a5a5',
    },
  },
}

export default StatisticExportButton
